<template>
  <div class="terms-page flex flex-col justify-center items-center p-16 gap-8 w-full">
    <div class="row flex gap-8 flex-wrap w-full justify-center">
      <div class="section p-8 flex flex-col gap-4 w-66">
        <h1 class="text-4xl my-4">Terms of Use</h1>
        <p class="effective-date"><em>Last updated: October 9, 2026</em></p>

        <p>
          These Terms of Use ("Terms") govern your use of <strong>Astro Path</strong>
          (the "App"), a mobile puzzle game developed by Alexandros Ploumis
          ("we", "us", "our"). By downloading, installing, or using the App,
          you agree to be bound by these Terms. If you do not agree, please do
          not use the App. How we handle your data is explained in our
          <router-link to="/privacy-policy">Privacy Policy</router-link>.
        </p>

        <h2>1. Use of the App</h2>
        <p>
          The App is provided for your personal, non-commercial entertainment.
          You are granted a limited, non-exclusive, non-transferable,
          revocable license to install and use the App on devices you own or
          control, solely for your own personal use, subject to these Terms.
        </p>

        <h2>2. In-App Purchases</h2>
        <p>
          The App offers an optional one-time in-app purchase ("Remove Ads")
          that disables advertising within the App. All purchases are
          processed through the app store you downloaded the App from (e.g.
          Google Play) and are subject to that store's own payment terms and
          refund policies. Purchases are final and non-refundable except where
          required by law or by the app store's own policies.
        </p>

        <h2>3. Advertising</h2>
        <p>
          Unless you have purchased the "Remove Ads" option, the App displays
          advertisements served by third-party advertising networks,
          including Google AdMob. These networks may collect and use data in
          accordance with their own privacy policies. Please refer to our
          <router-link to="/privacy-policy">Privacy Policy</router-link> for
          more information about how data is collected and used within the App.
        </p>

        <h2>4. Daily Puzzle and Online Features</h2>
        <p>
          The App includes an optional Daily Puzzle with leaderboards and
          friends. When you first open it, an anonymous online account is
          created for you automatically and you choose a username. You do not
          need to provide an email address, phone number, or password. Your
          username and your daily puzzle results are visible to other players
          on the leaderboards.
        </p>
        <p>
          Online features depend on an internet connection and on third-party
          services, and may be unavailable at times. We do not guarantee that a
          new Daily Puzzle will be published every day, and we may change,
          reset, or discontinue leaderboards, results, or any online feature at
          any time.
        </p>

        <h2>5. Usernames and Conduct</h2>
        <p>
          Your username must not be offensive, hateful, sexual, harassing,
          impersonate another person or a member of our team, or infringe
          anyone's rights. We may change or remove any username, and remove any
          related results or account, that we believe breaks these rules,
          without notice.
        </p>

        <h2>6. Fair Play</h2>
        <p>
          You agree not to cheat, including by using modified versions of the
          App, automation, bots, exploits, or by tampering with the App's
          clock, data, or network traffic to gain an advantage. We may remove
          any results we believe were not achieved fairly, and suspend or delete
          accounts involved.
        </p>

        <h2>7. Friends and Reporting</h2>
        <p>
          You can connect with other players by sharing your friend code.
          Anyone you give your code to can add you as a friend, and you can
          remove a friend at any time. If you see an inappropriate username on a
          leaderboard, you can report it from within the App; reported players
          are hidden from your leaderboards and reports are reviewed by us.
        </p>

        <h2>8. Deleting Your Online Data</h2>
        <p>
          You can delete your online account and all associated Daily Puzzle
          data at any time from within the App, or by contacting us. See the
          <router-link to="/privacy-policy#delete-data">Delete your data</router-link>
          section of our Privacy Policy for details.
        </p>

        <h2>9. Intellectual Property</h2>
        <p>
          The App, including its design, graphics, code, and content, is the
          property of Alexandros Ploumis and is protected by copyright and
          other intellectual property laws. You may not copy, modify,
          distribute, sell, or lease any part of the App, nor reverse
          engineer or attempt to extract its source code, except as permitted
          by applicable law.
        </p>

        <h2>10. No Warranty</h2>
        <p>
          The App is provided "as is" and "as available", without warranties
          of any kind, whether express or implied, including but not limited
          to warranties of merchantability, fitness for a particular purpose,
          or non-infringement. We do not guarantee that the App will be
          uninterrupted, error-free, or free of harmful components.
        </p>

        <h2>11. Limitation of Liability</h2>
        <p>
          To the fullest extent permitted by law, we shall not be liable for
          any indirect, incidental, special, consequential, or punitive
          damages, or any loss of data, revenue, or profits, arising out of
          or related to your use of, or inability to use, the App.
        </p>

        <h2>12. Changes to the App or These Terms</h2>
        <p>
          We may update, modify, or discontinue the App, or any part of it,
          at any time without prior notice. We may also revise these Terms
          from time to time; continued use of the App after any such changes
          constitutes your acceptance of the new Terms.
        </p>

        <h2>13. Termination</h2>
        <p>
          We reserve the right to suspend or terminate your access to the App
          or its online features at any time, without notice, if we believe you
          have violated these Terms.
        </p>

        <h2>14. Governing Law</h2>
        <p>
          These Terms shall be governed by and construed in accordance with
          the laws of Greece, without regard to its conflict of law provisions.
          Nothing in these Terms limits any rights you have under the mandatory
          consumer protection laws of your country of residence.
        </p>

        <h2>15. Contact Us</h2>
        <p>
          If you have any questions about these Terms, please contact us at
          <a href="mailto:ploumisalexandrosgr@gmail.com">ploumisalexandrosgr@gmail.com</a>.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TermsOfUse'
};
</script>

<style scoped>
  h1 {
    margin-bottom: 0;
  }

  h2 {
    font-size: 1.25rem;
    font-weight: bold;
    margin-top: 0.5rem;
  }

  .effective-date {
    opacity: 0.7;
  }

  .w-66 {
    width: 66%;
  }

  @media only screen and (max-width: 600px) {
    .terms-page.p-16 {
      padding: 1rem;
    }

    .w-66 {
      width: 100%;
    }
  }
</style>
